import { Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DwComponent,
  DwComponentType,
  DwEventService,
  DwItemAttribute,
  DwMenuActionType,
  DwMetaDataRoutingService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwRoutingService,
  DwSecurityUserService
} from '@devwareapps/devware-cap';

import {
  AppMetaData,
  AppMetaDataItemNames,
  AssignmentWithStudentGridQueryEntity,
  RoAssignmentStudentStatEntity
} from '../../../../meta-data/app-meta-data.service';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { gridAnnimations } from '../manage-users-grid-renderer/grid-animations.const';
import { AssignmentGridFilterAttributes, AssignmentGridRendererBaseComponent } from './assignment-grid-renderer-base.component';

type AssignmentGridRow = AssignmentWithStudentGridQueryEntity;

@DwComponent({
  key: 'assignments-grid-renderer',
  name: 'Assignments Grid Renderer',
  isGlobal: false,
  parentItemName: AppMetaData.Assignment.ItemDetail.itemName,
  componentType: DwComponentType.gridRenderer
})
@Component({
  selector: 'app-assignments-grid-renderer',
  templateUrl: './assignments-grid-renderer.component.html',
  styleUrls: ['./assignments-grid-renderer.component.scss'],
  animations: gridAnnimations
})
export class AssignmentsGridRendererComponent
  extends AssignmentGridRendererBaseComponent<AssignmentWithStudentGridQueryEntity> {

  public selectedAssignmentId: number | null = null;

  constructor(
    dwRoutingService: DwRoutingService,
    adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwEventService: DwEventService,
    dwMetaDataRoutingService: DwMetaDataRoutingService,
    router: Router,
    activatedRoute: ActivatedRoute,
    dwSecurityUserService: DwSecurityUserService
  ) {
    super(
      dwRoutingService,
      adminManagerService,
      dwMetaDataService,
      dwEventService,
      dwMetaDataRoutingService,
      router,
      activatedRoute,
      dwSecurityUserService
    );
  }

  public selectAssignment(assignment: AssignmentGridRow): void {
    this.selectedAssignmentId = assignment.AssignmentId || null;
    this.selectedItems = [assignment];
  }

  public editAssignment(assignment: AssignmentGridRow): void {
    if (!assignment?.AssignmentId) {
      return;
    }

    this.selectAssignment(assignment);
    this.dwMetaDataRoutingService.routeToEditOrViewWithKey(
      this.activatedRoute,
      AppMetaDataItemNames.Assignment,
      assignment.AssignmentId,
      DwMenuActionType.edit
    );
  }

  public trackByAssignment(index: number, item: AssignmentGridRow): string | number {
    return item.AssignmentId || index;
  }

  public getCompletedDate(assignment: AssignmentGridRow): string | null {
    if (assignment.CompletedDateTime) {
      return assignment.CompletedDateTime;
    }

    const completedDates = (assignment.AssignmentStudent || [])
      .map(student => student.CompletedDateTime)
      .filter(completedDate => !!completedDate);

    if (completedDates.length === 0 || completedDates.length !== (assignment.AssignmentStudent || []).length) {
      return null;
    }

    return completedDates.sort().slice(-1)[0] || null;
  }

  public getCompletedPercent(assignment: AssignmentGridRow): string {
    const completedPercent = this.getAssignmentStudentStat(assignment)?.CompletedPercent;

    if (completedPercent !== null && completedPercent !== undefined) {
      return completedPercent.toString();
    }

    const students = assignment.AssignmentStudent || [];

    if (students.length === 0) {
      return '0';
    }

    const completedCount = students.filter(student => !!student.CompletedDateTime).length;

    return Math.round((completedCount / students.length) * 100).toString();
  }

  public getStudentDisplayNames(assignment: AssignmentGridRow): string {
    const studentNames = (assignment.AssignmentStudent || [])
      .map(student => student.StudentIdDisplay)
      .filter(studentName => !!studentName);
    const uniqueStudentNames = Array.from(new Set(studentNames));

    return uniqueStudentNames.join(', ') || '-';
  }

  protected getTextSearchAttributes(): (string | DwItemAttribute)[] {
    return [
      AppMetaData.Queries.AssignmentWithStudentGrid.Attributes.Students,
      AppMetaData.Queries.AssignmentWithStudentGrid.Attributes.CourseIdDisplay,
      AppMetaData.Queries.AssignmentWithStudentGrid.Attributes.InstructorIdDisplay
    ];
  }

  protected getFilterAttributes(): AssignmentGridFilterAttributes {
    const attrs = AppMetaData.Queries.AssignmentWithStudentGrid.Attributes;

    return {
      courseId: attrs.CourseId,
      instructorId: attrs.InstructorId,
      groupId: attrs.FlightSchoolGroupId,
      statusId: attrs.AssignmentStatusId
    };
  }

  private getAssignmentStudentStat(assignment: AssignmentGridRow): RoAssignmentStudentStatEntity | null {
    return assignment.RoAssignmentStudentStat?.[0] || null;
  }
}
